// vendorValidation.ts
import { VendorCreate } from "@/types";
import {
  validateRequired,
  validateMinValue,
  validateEmail,
  validatePhone,
  validateZipCodeFormat,
} from "../helpers/validationHelpers";

export const validateVendor = (values: VendorCreate): Record<string, string> => {
  const errors: Record<string, string> = {};

  // --- Required Top-Level Fields ---
  validateRequired(values.company_name, "company_name", errors);

  // --- Business Logic Validations ---

  // At least one vendor type should be selected
  if (!values.is_sl_carrier && !values.is_tpa && !values.is_rx_vendor) {
    errors["is_sl_carrier"] = "At least one vendor type must be selected (SL Carrier, TPA, or Rx Vendor)";
  }

  // If TPA is BUCA, TPA must be selected
  if (values.tpa_is_buca === true && values.is_tpa !== true) {
    errors["tpa_is_buca"] = "TPA must be selected if TPA is BUCA";
  }

  // If parent company is specified, has_children_co should probably be true
  // (this is more of a warning, but we can validate it)
  if (values.provider_parent_co && !values.has_children_co) {
    errors["has_children_co"] = "Should be enabled when parent company is specified";
  }

  // --- External Identifier Validations ---
  // Ensure numeric identifiers are positive if provided
  if (values.org_id !== undefined) {
    validateMinValue(values.org_id, "org_id", errors, 0);
  }
  if (values.license_number !== undefined) {
    validateMinValue(values.license_number, "license_number", errors, 0);
  }
  if (values.fein !== undefined) {
    validateMinValue(values.fein, "fein", errors, 0);
  }
  if (values.naic_code !== undefined) {
    validateMinValue(values.naic_code, "naic_code", errors, 0);
  }

  // --- Contact Information Validations ---
  const primaryContact = values.primary_contact;
  const claimsContact = values.claims_contact;

  // Validate primary contact email if provided
  if (primaryContact?.email) {
    validateEmail(primaryContact.email, "primary_contact.email", errors);
  }

  // Validate claims contact email if provided
  if (claimsContact?.email) {
    validateEmail(claimsContact.email, "claims_contact.email", errors);
  }

  // Validate contact phone format if provided
  if (primaryContact?.phone) {
    validatePhone(primaryContact.phone, "primary_contact.phone", errors);
  }
  if (claimsContact?.phone) {
    validatePhone(claimsContact.phone, "claims_contact.phone", errors);
  }

  // --- Address Validation ---
  if (values.address?.zip) {
    validateZipCodeFormat(values.address.zip, "address.zip", errors);
  }

  // --- Website Validation ---
  if (values.website) {
    // Basic URL validation
    try {
      new URL(values.website);
    } catch {
      errors["website"] = "Must be a valid URL (include http:// or https://)";
    }
  }

  return errors;
};
